import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RandomContactHistory = ({ 
  history = [], 
  onContactClick, 
  onClearHistory,
  className = '' 
}) => {
  const [showAll, setShowAll] = useState(false);
  
  const displayHistory = showAll ? history : history?.slice(0, 5);

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'sms': return 'MessageSquare';
      case 'email': return 'Mail';
      case 'whatsapp': return 'MessageCircle';
      default: return 'MessageSquare';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'sent': return 'text-success';
      case 'scheduled': return 'text-warning';
      case 'skipped': return 'text-text-secondary';
      default: return 'text-text-secondary';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date?.getFullYear() !== now?.getFullYear() ? 'numeric' : undefined
    });
  };

  if (history?.length === 0) {
    return (
      <div className={`bg-surface border border-border rounded-xl p-6 text-center ${className}`}>
        <Icon name="History" size={32} className="mx-auto mb-3 text-text-secondary opacity-50" />
        <h3 className="text-lg font-medium text-text-primary mb-2">No History Yet</h3>
        <p className="text-text-secondary">
          Your random contact selections and actions will appear here
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-surface border border-border rounded-xl shadow-card ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
            <Icon name="History" size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">
              Recent Activity
            </h3>
            <p className="text-sm text-text-secondary">
              {history?.length} contact{history?.length !== 1 ? 's' : ''} selected
            </p>
          </div>
        </div>

        {history?.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearHistory}
            iconName="Trash2"
            iconPosition="left"
          >
            Clear
          </Button>
        )}
      </div>
      {/* History List */}
      <div className="divide-y divide-border">
        {displayHistory?.map((item, index) => (
          <div key={item?.id || index} className="p-4 hover:bg-muted transition-micro">
            <div className="flex items-start space-x-4">
              {/* Contact Avatar */}
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                {item?.contact?.avatar ? (
                  <Image 
                    src={item?.contact?.avatar} 
                    alt={item?.contact?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <Icon name="User" size={20} className="text-text-secondary" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <button
                      onClick={() => onContactClick(item?.contact)}
                      className="font-medium text-text-primary hover:text-primary transition-micro"
                    >
                      {item?.contact?.name}
                    </button>
                    {item?.contact?.company && (
                      <p className="text-sm text-text-secondary">
                        {item?.contact?.company}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-text-secondary flex-shrink-0">
                    {formatDate(item?.timestamp)}
                  </span>
                </div>

                {/* Action Details */}
                <div className="flex items-center space-x-4 text-sm">
                  <div className={`flex items-center space-x-1 ${getActionColor(item?.action)}`}>
                    <Icon 
                      name={item?.action === 'sent' ? 'Check' : item?.action === 'scheduled' ? 'Clock' : 'SkipForward'} 
                      size={14} 
                    />
                    <span className="capitalize">{item?.action}</span>
                  </div>

                  {item?.messageType && (
                    <div className="flex items-center space-x-1 text-text-secondary">
                      <Icon name={getMessageTypeIcon(item?.messageType)} size={14} />
                      <span className="capitalize">{item?.messageType}</span>
                    </div>
                  )}

                  {item?.scheduled && (
                    <div className="flex items-center space-x-1 text-warning">
                      <Icon name="Calendar" size={14} />
                      <span>
                        {new Date(item.scheduledFor)?.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Message Preview */}
                {item?.messagePreview && (
                  <p className="text-sm text-text-secondary mt-2 line-clamp-2">
                    "{item?.messagePreview}"
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Show More/Less */}
      {history?.length > 5 && (
        <div className="p-4 border-t border-border text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAll(!showAll)}
            iconName={showAll ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
          >
            {showAll ? 'Show Less' : `Show ${history?.length - 5} More`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default RandomContactHistory;